import { axios } from "@/client/libs/axios";
import { getPersistentFingerprint } from "@/client/utils/fingerprint";

import { tokenConfigService, DEFAULT_CONFIG } from "./token-config.service";

/**
 * Custom error for session limit exceeded — allows callers to detect
 * this specific case and show appropriate UI (e.g. sign-up modal).
 */
export class SessionLimitError extends Error {
  readonly code = "SESSION_LIMIT_EXCEEDED";
  constructor(message?: string) {
    super(message || "You've used all your free sessions. Sign up for free to continue!");
    this.name = "SessionLimitError";
  }
}

// For backward compatibility, use DEFAULT_CONFIG values as fallback
const DEFAULT_GUEST_TOKEN_BALANCE = DEFAULT_CONFIG.GUEST_TOKEN_BALANCE;

// Unified event name for all token updates across the app
export const TOKEN_BALANCE_EVENT = "stylingcv:token-balance-updated";

/**
 * Sanitize numeric values to prevent NaN, Infinity, and negative values
 * @param val - The value to sanitize
 * @param fallback - Fallback value if invalid (default: 0)
 * @param allowNegative - Whether to allow negative values (default: false)
 */
const sanitizeNumber = (val: unknown, fallback = 0, allowNegative = false): number => {
  const num = Number(val);
  if (!Number.isFinite(num)) return fallback;
  if (!allowNegative && num < 0) return fallback;
  return num;
};

/**
 * Global Token Event System
 * Single source of truth for token balance updates across the entire application
 */
class TokenEventEmitter extends EventTarget {
  private lastBalance: number | null = null;

  /**
   * Dispatch token balance update to all listeners
   * Uses both a local EventTarget event and a window-level CustomEvent
   */
  dispatch(balance: number) {
    // Avoid redundant updates
    if (this.lastBalance === balance) return;
    this.lastBalance = balance;

    const detail = { balance, timestamp: Date.now() };

    // Emit on this event target (for direct listeners)
    this.dispatchEvent(new CustomEvent("tokenUpdate", { detail: balance }));

    // Emit on window for global listeners (Zustand store, TokenContext, etc.)
    window.dispatchEvent(new CustomEvent(TOKEN_BALANCE_EVENT, { detail }));

  }

  /**
   * Get the last known balance (for components that need immediate value)
   */
  getLastBalance(): number | null {
    return this.lastBalance;
  }

  /**
   * Reset the last balance (useful for logout)
   */
  reset() {
    this.lastBalance = null;
  }
}

export const tokenEventEmitter = new TokenEventEmitter();

// Centralized token service interface
export type TokenBalance = {
  balance: number;
  used: number;
  limit: number;
  percentage: number;
  lastUpdated: number;
};

export type TokenConsumptionResult = {
  success: boolean;
  newBalance: number;
  error?: string;
};

/**
 * Centralized Token Management Service
 * Single source of truth for all token operations
 */
class CentralizedTokenService {
  private cache = new Map<string, { data: TokenBalance; expiry: number }>();
  private readonly CACHE_TTL = 30 * 1000; // 30 seconds - balance between freshness and API calls
  private guestSessionInitPromise: Promise<void> | null = null; // Prevent race conditions
  private guestSessionLastInit: number = 0; // Prevent initialization loops
  private readonly GUEST_SESSION_COOLDOWN = 5 * 60 * 1000; // 5 minutes

  // Request deduplication for token balance fetches
  private tokenBalanceFetchPromise: Promise<TokenBalance> | null = null;
  private tokenBalanceFetchKey: string | null = null;
  private readonly FETCH_DEDUP_WINDOW = 500; // 500ms deduplication window

  /**
   * Get user's current token balance with caching and request deduplication
   * Prevents multiple concurrent requests for the same balance
   */
  async getTokenBalance(isLoggedIn: boolean, forceRefresh = false): Promise<TokenBalance> {
    const cacheKey = isLoggedIn ? "user" : "guest";
    const now = Date.now();

    // Check cache first
    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && cached.expiry > now) {
        return cached.data;
      }
    }

    // Request deduplication: if there's an in-flight request for the same data, reuse it
    const fetchKey = `${cacheKey}-${forceRefresh}`;
    if (
      this.tokenBalanceFetchPromise &&
      this.tokenBalanceFetchKey === fetchKey &&
      now - (this.cache.get(cacheKey)?.expiry ?? 0) < this.FETCH_DEDUP_WINDOW
    ) {
      return this.tokenBalanceFetchPromise;
    }

    // Create new fetch promise
    this.tokenBalanceFetchKey = fetchKey;
    this.tokenBalanceFetchPromise = (async () => {
      try {
        // NOTE: Mock removed - now properly calling server APIs
        const tokenBalance = isLoggedIn
          ? await this.fetchAuthenticatedUserTokens()
          : await this.getGuestTokenBalance();

        // Cache the result
        this.cache.set(cacheKey, {
          data: tokenBalance,
          expiry: now + this.CACHE_TTL,
        });

        // Emit token update event
        tokenEventEmitter.dispatch(tokenBalance.balance);

        return tokenBalance;
      } finally {
        // Clear the promise after a short delay to allow React StrictMode double-invocation
        // but prevent long-term deduplication issues
        setTimeout(() => {
          if (this.tokenBalanceFetchKey === fetchKey) {
            this.tokenBalanceFetchPromise = null;
            this.tokenBalanceFetchKey = null;
          }
        }, this.FETCH_DEDUP_WINDOW);
      }
    })();

    return this.tokenBalanceFetchPromise;
  }

  /**
   * Fetch tokens for authenticated users - uses aiTokenBalance field only
   */
  private async fetchAuthenticatedUserTokens(): Promise<TokenBalance> {
    try {
      // Use /user/ai-tokens API (reads directly from aiTokenBalance field)
      const response = await axios.get("/user/ai-tokens");
      const data = response.data;

      return {
        balance: sanitizeNumber(data.remaining), // Sanitized to prevent NaN/Infinity/negative
        used: sanitizeNumber(data.used),
        limit: sanitizeNumber(data.limit),
        percentage: Math.min(100, Math.max(0, sanitizeNumber(data.percentage))), // Clamped 0-100
        lastUpdated: Date.now(),
      };
    } catch (error) {
      console.error("Failed to fetch token balance from aiTokenBalance field", error);
      throw new Error("Failed to fetch token balance");
    }
  }

  /**
   * Initialize guest session (with race condition protection and retry)
   */
  private async initializeGuestSession(): Promise<void> {
    const now = Date.now();

    // Prevent initialization loops with cooldown (time-based only, not promise-dependent)
    if (now - this.guestSessionLastInit < this.GUEST_SESSION_COOLDOWN) {
      return;
    }

    // If already initializing, wait for that to complete
    if (this.guestSessionInitPromise) {
      return this.guestSessionInitPromise;
    }

    // Create new initialization promise with retry logic
    this.guestSessionInitPromise = (async () => {
      const maxRetries = 2;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const fingerprint = getPersistentFingerprint();
          const response = await axios.post("/guest/session", { fingerprint }, { withCredentials: true });

          // Verify the session was created
          if (response.data?.success && response.data?.guestId) {
            return;
          }

          throw new Error("Invalid session response");
        } catch (error: any) {
          lastError = error;
          console.error(
            `Failed to initialize guest session (attempt ${attempt + 1}):`,
            error?.message,
          );

          // Don't retry on 429 or 400 — rate limit / session limit won't clear by retrying
          if (error?.response?.status === 429 || error?.response?.status === 400) {
            throw error;
          }

          // Wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
          }
        }
      }

      // All retries failed
      throw lastError || new Error("Failed to initialize guest session after retries");
    })();

    try {
      // Await the promise BEFORE setting lastInit so that a failed attempt
      // (e.g. 400 SESSION_LIMIT_EXCEEDED) does NOT lock out the next call for 5 minutes.
      // lastInit is only set on SUCCESS, allowing an immediate retry if the first attempt
      // fails transiently (network error, etc.) without infinite-looping on hard limits.
      const result = await this.guestSessionInitPromise;
      this.guestSessionLastInit = Date.now(); // Only set on success
      return result;
    } finally {
      // Clear the promise after completion (success or failure)
      this.guestSessionInitPromise = null;
    }
  }

  /**
   * Check if current page is an auth page where guest sessions aren't needed
   */
  private isAuthPage(): boolean {
    const path = window.location.pathname;
    return path.startsWith("/auth/");
  }

  /**
   * Get guest token balance from server (secure implementation with session protection)
   */
  private async getGuestTokenBalance(): Promise<TokenBalance> {
    try {
      // Skip guest session creation on auth pages — no need for tokens there
      if (this.isAuthPage()) {
        return {
          balance: DEFAULT_GUEST_TOKEN_BALANCE,
          used: 0,
          limit: DEFAULT_GUEST_TOKEN_BALANCE,
          percentage: 0,
          lastUpdated: Date.now(),
        };
      }

      // Initialize guest session if needed (with race condition protection)
      // This MUST succeed for the cookie to be set
      await this.initializeGuestSession();

      // Get token usage from server
      const response = await axios.get("/guest/tokens", { withCredentials: true });

      return {
        balance: sanitizeNumber(response.data.remaining),
        used: sanitizeNumber(response.data.used),
        limit: sanitizeNumber(response.data.limit),
        percentage: Math.min(100, Math.max(0, sanitizeNumber(response.data.percentage))),
        lastUpdated: Date.now(),
      };
    } catch (error: any) {
      console.error("Failed to fetch guest token balance from server", error);

      // Detect session limit exceeded from server response
      const serverError = error?.response?.data?.error;
      if (serverError === "SESSION_LIMIT_EXCEEDED") {
        throw new SessionLimitError(error?.response?.data?.message);
      }

      // If session initialization failed (no cookie set), we should NOT return fake tokens
      // This prevents the user from submitting forms without a valid session
      const isSessionError =
        error?.message?.includes("session") ||
        error?.response?.status === 401 ||
        error?.response?.status === 400;

      if (isSessionError) {
        // Check if the underlying cause was a session limit
        if (error instanceof SessionLimitError) {
          throw error;
        }
        // Re-throw to surface the error - don't give fake tokens without a session
        throw new Error("Unable to initialize session. Please refresh the page.");
      }

      // Only fallback for non-critical errors (e.g., network hiccup on token fetch)
      // The session cookie should already be set from initializeGuestSession
      return {
        balance: DEFAULT_GUEST_TOKEN_BALANCE,
        used: 0,
        limit: DEFAULT_GUEST_TOKEN_BALANCE,
        percentage: 0,
        lastUpdated: Date.now(),
      };
    }
  }

  /**
   * Consume tokens for a feature with proper error handling
   */
  async consumeTokens(
    amount: number,
    isLoggedIn: boolean,
    feature: string,
  ): Promise<TokenConsumptionResult> {
    // Input validation - CRITICAL: Prevent negative/invalid token consumption
    if (!Number.isFinite(amount) || amount <= 0 || !Number.isInteger(amount)) {
      console.error(`[TokenService] Invalid token amount: ${amount}`);
      return {
        success: false,
        newBalance: 0,
        error: "Invalid token amount. Must be a positive integer.",
      };
    }

    try {
      // Get current balance first
      const currentBalance = await this.getTokenBalance(isLoggedIn, true);

      if (currentBalance.balance < amount) {
        return {
          success: false,
          newBalance: currentBalance.balance,
          error: `You've run out of credits. You need ${amount} tokens but only have ${currentBalance.balance}. Upgrade to continue.`,
        };
      }

      if (isLoggedIn) {
        // For authenticated users, deduct on server
        await axios.patch("/user/ai-tokens/track", { tokens: amount });

        // Clear cache to force refresh on next call
        this.cache.delete("user");

        // Get updated balance
        const newBalance = await this.getTokenBalance(isLoggedIn, true);

        return {
          success: true,
          newBalance: newBalance.balance,
        };
      } else {
        // For guests, ensure session is initialized before consuming tokens
        await this.initializeGuestSession();

        // For guests, consume tokens on server (secure implementation)
        await axios.patch(
          "/guest/tokens/consume",
          {
            tokens: amount,
            operation: feature,
          },
          { withCredentials: true },
        );

        // Clear cache to force refresh on next call
        this.cache.delete("guest");

        // Get updated balance from server
        const updatedBalance = await this.getTokenBalance(isLoggedIn, true);

        return {
          success: true,
          newBalance: updatedBalance.balance,
        };
      }
    } catch (error) {
      console.error("Error consuming tokens:", error);
      // Try to get current balance, fallback to -1 to indicate unknown state
      const cachedBalance = this.cache.get(isLoggedIn ? "user" : "guest")?.data?.balance ?? -1;
      return {
        success: false,
        newBalance: cachedBalance,
        error: "Network error. Please check your connection and try again.",
      };
    }
  }

  /**
   * Add tokens to user balance (for purchases)
   * After a successful payment, this refreshes the balance and emits update event
   */
  async addTokens(amount: number, isLoggedIn: boolean): Promise<boolean> {
    try {
      if (isLoggedIn) {
        // For authenticated users, the server handles this via Stripe/payments
        // Clear cache and force refresh to get new balance
        this.cache.delete("user");
        
        // Fetch the updated balance and emit event for UI sync
        const newBalance = await this.getTokenBalance(true, true);
        tokenEventEmitter.dispatch(newBalance.balance);
        
        return true;
      } else {
        // For guests, token additions are not supported (they should register)
        return false;
      }
    } catch (error) {
      console.error("Error adding tokens:", error);
      return false;
    }
  }

  /**
   * Clear all caches (useful for logout)
   * Also resets the event emitter to prevent stale balance on new login
   */
  clearCache() {
    this.cache.clear();
    tokenEventEmitter.reset();
  }

  /**
   * Get token cost for a specific feature
   * Now reads from database-backed config service
   */
  getTokenCost(feature: string): number {
    // Map feature names to config keys
    const featureToConfigKey: Record<string, string> = {
      RESUME_GENERATION: "RESUME_GENERATION_COST",
      SUMMARY_GENERATION: "SUMMARY_GENERATION_COST",
      COVER_LETTER: "COVER_LETTER_COST",
      PDF_EXPORT: "PDF_EXPORT_COST",
      WORD_EXPORT: "WORD_EXPORT_COST",
      ADVANCED_RESUME_ANALYSIS: "ADVANCED_RESUME_ANALYSIS_COST",
      ANALYTICS: "ANALYTICS_COST",
      JOB_FIT_SCORE: "JOB_FIT_SCORE_COST",
      ATS_SCORE: "ATS_SCORE_COST",
      AI_SUMMARY: "AI_SUMMARY_COST",
      IMPROVE_WRITING: "IMPROVE_WRITING_COST",
      FIX_SPELLING_GRAMMAR: "FIX_SPELLING_GRAMMAR_COST",
      CHANGE_TONE: "CHANGE_TONE_COST",
      BOOSTER: "BOOSTER_COST",
      TRAJECTORY: "TRAJECTORY_COST",
      LOCALIZATION: "LOCALIZATION_COST",
      LINKEDIN_REWRITE: "LINKEDIN_REWRITE_COST",
      MARKET_SCOUT: "MARKET_SCOUT_COST",
      EXTRACT_PDF_RESUME: "EXTRACT_PDF_RESUME_COST",
      GENERATE_RESUME: "GENERATE_RESUME_COST",
      FORMAT_RESUME: "FORMAT_RESUME_COST",
      INTERVIEW_QUESTION: "INTERVIEW_QUESTION_COST",
      AI_URL_JOB_DETAILS: "AI_URL_JOB_DETAILS_COST",
    };

    const configKey = featureToConfigKey[feature] || `${feature}_COST`;
    return (
      tokenConfigService.getValueSync(configKey as any) ||
      DEFAULT_CONFIG[configKey as keyof typeof DEFAULT_CONFIG] ||
      0
    );
  }

  /**
   * Check if user can afford a specific feature
   */
  async canAfford(feature: string, isLoggedIn: boolean): Promise<boolean> {
    const cost = this.getTokenCost(feature);
    const balance = await this.getTokenBalance(isLoggedIn);
    return balance.balance >= cost;
  }

  // Sync methods removed - no longer needed with single aiTokenBalance source
}

// Export singleton instance
export const centralizedTokenService = new CentralizedTokenService();

/**
 * Hook for easy React integration
 */
export const useCentralizedTokens = () => {
  return {
    getTokenBalance: centralizedTokenService.getTokenBalance.bind(centralizedTokenService),
    consumeTokens: centralizedTokenService.consumeTokens.bind(centralizedTokenService),
    addTokens: centralizedTokenService.addTokens.bind(centralizedTokenService),
    clearCache: centralizedTokenService.clearCache.bind(centralizedTokenService),
    getTokenCost: centralizedTokenService.getTokenCost.bind(centralizedTokenService),
    canAfford: centralizedTokenService.canAfford.bind(centralizedTokenService),
  };
};
